import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { useConfig } from "../lib/useConfig";
import { useLang } from "../lib/i18n";
import { KpiCard, PageHeader, pct, num } from "../lib/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { FileCheck2, Percent, Siren, Microscope, Users, Building2 } from "lucide-react";
import { ZoneMap } from "../components/ZoneMap";

export default function Provinces() {
  const { data: cfg } = useConfig();
  const { t } = useLang();
  const provinces = Object.keys(cfg?.geo || {});
  const [province, setProvince] = useState("");
  useEffect(() => { if (!province && provinces.length) setProvince(provinces[0]); }, [provinces, province]);
  const [sortKey, setSortKey] = useState("reports");

  const { data } = useQuery({
    queryKey: ["prov", province],
    queryFn: async () => (await api.get(`/profiles/province/${encodeURIComponent(province)}`)).data,
    enabled: !!province,
  });
  const m = data?.metrics || {};
  const zones = [...(data?.health_zones || [])].sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));

  return (
    <div data-testid="provinces-page">
      <PageHeader title={t("Profil Épidémiologique Provincial")} subtitle={t("Agrégation de toutes les zones de santé")}>
        <Select value={province} onValueChange={setProvince}>
          <SelectTrigger className="w-48" data-testid="prov-select"><SelectValue placeholder={t("Choisir")} /></SelectTrigger>
          <SelectContent>{provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      {data?.empty ? <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-400">{t("Aucune donnée.")}</div> : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <KpiCard label="Rapports reçus" value={num(data.reports_received)} icon={FileCheck2} />
            <KpiCard label="Complétude" value={pct(data.completeness)} icon={Percent} tone="teal" />
            <KpiCard label="Zones actives" value={num(data.health_zones?.length)} icon={Building2} />
            <KpiCard label="Alertes reçues" value={num(m.alerts_received)} icon={Siren} tone="amber" />
            <KpiCard label="Cas suspects" value={num(m.suspected_cases)} icon={Microscope} tone="red" />
            <KpiCard label="Contacts suivis" value={num(m.contacts_followed)} icon={Users} />
          </div>

          <div className="grid lg:grid-cols-2 gap-5 mt-5">
            <ZoneMap province={province} height={280} testid="prov-map" />
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">{t("Alertes par Zone de Santé")}</h3>
              <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.health_zones} margin={{ bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="health_zone" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} interval={0} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip />
                <Bar dataKey="alerts_received" name={t("Alertes")} fill="#D97706" radius={[2, 2, 0, 0]} />
                <Bar dataKey="suspected_cases" name="Cas suspects" fill="#DC2626" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 mt-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-800">Classement des zones</h3>
              <Select value={sortKey} onValueChange={setSortKey}>
                <SelectTrigger className="w-48" data-testid="prov-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="reports">{t("Trier par rapports")}</SelectItem>
                  <SelectItem value="alerts_received">{t("Trier par alertes")}</SelectItem>
                  <SelectItem value="suspected_cases">{t("Trier par cas suspects")}</SelectItem>
                  <SelectItem value="completeness">{t("Trier par complétude")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>{t("Zone de Santé")}</TableHead><TableHead className="text-right">{t("Rapports")}</TableHead><TableHead className="text-right">{t("Complétude")}</TableHead><TableHead className="text-right">{t("Alertes")}</TableHead><TableHead className="text-right">{t("Cas suspects")}</TableHead></TableRow></TableHeader>
              <TableBody>{zones.map((z) => (
                <TableRow key={z.health_zone}><TableCell className="font-medium">{z.health_zone}</TableCell>
                  <TableCell className="text-right">{z.reports}</TableCell><TableCell className="text-right">{pct(z.completeness)}</TableCell>
                  <TableCell className="text-right">{z.alerts_received}</TableCell><TableCell className="text-right">{z.suspected_cases}</TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
